import {
  IBFBatchRecordParsingDefinition,
  IBFRecordParsingDefinition,
} from "@/lib/drivers/insulet/IBFRecords/RecordParser";

type ProfileIndexRaw = 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21;

type ProfileIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | "CARBS_RATIO" | "INSULIN_SENSITIVITY" | "BG_TARGET" | "BG_THRESHOLD";

type IBFProfileItem = {
  profileIndex: ProfileIndex;

  errorCode: number;
  operationTime: number;
  steps: Record<number, number>;
};

const valueToProfileIndex: Record<ProfileIndexRaw, ProfileIndex> = {
  11: "CARBS_RATIO",
  12: "INSULIN_SENSITIVITY",
  13: "BG_TARGET",
  14: "BG_THRESHOLD",
  15: 0,
  16: 1,
  17: 2,
  18: 3,
  19: 4,
  20: 5,
  21: 6,
};

const IBF_PROFILE_ITEM = {
  value: 0,
  name: "IBF_PROFILE_ITEM",
  size: 157,
  parse: (record, info) => {
    const profileIndex = valueToProfileIndex[record.nextUInt8() as ProfileIndexRaw];
    let valueFactor = 1;
    if (info.isOmnipodDash && profileIndex === "CARBS_RATIO") {
      // Omnipod Dash carbs ratio is multiplied by 10 for some reason
      valueFactor = 0.1;
    }
    record.skip(6);
    const errorCode = record.nextUInt16BE();
    const operationTime = record.nextUInt32LE();
    const steps: Record<number, number> = {};
    for (let i = 0; i < 48; i++) {
      const key = i * 30 * 60 * 1000;
      steps[key] = record.nextInt32LE() * valueFactor;
    }

    return {
      profileIndex,
      errorCode,
      operationTime,
      steps,
    };
  },
} satisfies IBFRecordParsingDefinition<IBFProfileItem>;

export type IBFProfileSegment = {
  start: number;
  end: number;
  basalInsulin: number;
  carbsRatio: number;
  insulinSensitivity: number;
  targetBG: number;
  thresholdBG: number;
};

export type IBFPumpProfileList = {
  index: number;
  steps: IBFProfileSegment[];
}[];

type SharedSegmentSettings = {
  carbsRatio?: Record<number, number>;
  insulinSensitivity?: Record<number, number>;
  targetBG?: Record<number, number>;
  thresholdBG?: Record<number, number>;
};

/**
 * Generates a list of profile segments for a given set of pump profiles,
 * shared segment settings, and basal insulin segments.
 *
 * @param {number[]} profiles - An array of profile indices to process.
 * @param {SharedSegmentSettings} shared - An object containing shared settings
 * such as carbohydrate ratio, insulin sensitivity, target blood glucose (BG),
 * and threshold blood glucose (BG) values for each time period.
 * @param {Record<number, number>[]} basalSegments - An array of records where
 * each record maps a timestamp (in milliseconds) to a basal insulin rate for
 * each profile index.
 *
 * @returns {IBFPumpProfileList} A list of pump profiles including their indices
 * and step-wise breakdown of settings such as basal insulin, carbohydrate ratios,
 * insulin sensitivity, target blood glucose, and threshold blood glucose levels
 * for 30-minute intervals throughout a day.
 */
const makeSegments = (
  profiles: number[],
  shared: SharedSegmentSettings,
  basalSegments: Record<number, number>[],
): IBFPumpProfileList => {
  const result: IBFPumpProfileList = profiles.map(profileIndex => ({
    index: profileIndex,
    steps: [],
  }));

  for (let i = 0; i < 48; i++) {
    const start = i * 30 * 60 * 1000;
    const end = start + 30 * 60 * 1000;
    const carbsRatio = shared.carbsRatio?.[start] ?? 0;
    const insulinSensitivity = shared.insulinSensitivity?.[start] ?? 0;
    const targetBG = shared.targetBG?.[start] ?? 0;
    const thresholdBG = shared.thresholdBG?.[start] ?? 0;
    const basalInsulin = basalSegments.map(step => step[start]);
    for (let profileIndex = 0; profileIndex < result.length; profileIndex++) {
      result[profileIndex].steps.push({
        start,
        end,
        basalInsulin: basalInsulin[profileIndex] / 100.0,
        carbsRatio,
        insulinSensitivity,
        targetBG,
        thresholdBG,
      });
    }
  }
  // Set end 1 ms before new day
  for (const profile of result) {
    const length = profile.steps.length;
    const lastStep = profile.steps[length - 1];
    lastStep.end = lastStep.end - 1;
  }
  return result;
};

/**
 * Compares two IBFProfileSegment objects to determine if they have identical data.
 *
 * @param {IBFProfileSegment} a - The first profile segment to compare.
 * @param {IBFProfileSegment} b - The second profile segment to compare.
 * @returns {boolean} Returns true if all properties of both profile segments are equal, otherwise false.
 */
const segmentsHaveSameData = (a: IBFProfileSegment, b: IBFProfileSegment) => {
  return (
    a.basalInsulin === b.basalInsulin &&
    a.carbsRatio === b.carbsRatio &&
    a.insulinSensitivity === b.insulinSensitivity &&
    a.targetBG === b.targetBG &&
    a.thresholdBG === b.thresholdBG
  );
};

/**
 * Merges consecutive segments with identical data into a single segment.
 *
 * This function iterates through an array of segments and checks if consecutive segments
 * have identical data. If so, it merges them by extending the `end` property of the
 * current segment. Non-matching segments are added to the result list individually.
 *
 * @param {IBFProfileSegment[]} segments - An array of profile segments to be merged.
 * @returns {IBFProfileSegment[]} A new array of profile segments where consecutive
 * identical segments are merged.
 */
const mergeDuplicatedSegments = (segments: IBFProfileSegment[]): IBFProfileSegment[] => {
  if (segments.length === 0) {
    return [];
  }
  let currentSegment = segments[0];
  const result: IBFProfileSegment[] = [];
  for (let i = 1; i < segments.length; i++) {
    const nextSegment = segments[i];
    if (segmentsHaveSameData(currentSegment, nextSegment)) {
      currentSegment.end = nextSegment.end;
    } else {
      result.push(currentSegment);
      currentSegment = nextSegment;
    }
  }
  result.push(currentSegment);
  return result;
};

export const IBF_PUMP_PROFILE_LIST = {
  value: 6,
  name: "IBF_PUMP_PROFILE_LIST",
  recordsCount: 11,
  definition: IBF_PROFILE_ITEM,
  parse: logs => {
    const carbsRatioSteps = logs.find(log => log.data.profileIndex === "CARBS_RATIO")?.data.steps;
    const insulinSensitivitySteps = logs.find(log => log.data.profileIndex === "INSULIN_SENSITIVITY")?.data.steps;
    const bgTargetSteps = logs.find(log => log.data.profileIndex === "BG_TARGET")?.data.steps;
    const bgThresholdSteps = logs.find(log => log.data.profileIndex === "BG_THRESHOLD")?.data.steps;
    const profiles = logs
      .filter(log => typeof log.data.profileIndex === "number")
      .sort((a, b) => (a.data.profileIndex as number) - (b.data.profileIndex as number));

    const basalSteps = profiles.map(log => log.data.steps);

    const segments = makeSegments(
      profiles.map(log => log.data.profileIndex as number),
      {
        carbsRatio: carbsRatioSteps,
        insulinSensitivity: insulinSensitivitySteps,
        targetBG: bgTargetSteps,
        thresholdBG: bgThresholdSteps,
      },
      basalSteps,
    );

    return segments.map(segment => ({
      ...segment,
      steps: mergeDuplicatedSegments(segment.steps),
    }));
  },
} satisfies IBFBatchRecordParsingDefinition<IBFProfileItem, IBFPumpProfileList>;
